import { Badge, Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useUpdateCaseStatus } from '../api/cases';
import { parseMutationError } from '../api/errors';
import { useHasPermission } from '../api/session';
import { ReasonConfirmModal } from './reason-confirm-modal';

const STATUS_COLOURS: Record<string, string> = {
  open: 'blue',
  awaiting_histology: 'yellow',
  completed: 'green',
};

const ALL_STATUSES = [
  { value: 'open', label: 'open' },
  { value: 'awaiting_histology', label: 'awaiting_histology' },
  { value: 'completed', label: 'completed' },
];

interface Props {
  caseId: string;
  status: string;
}

/**
 * Case status badge with an adjacent manual status-override control
 * (case-triage capability 4). Any-to-any among the 3 known statuses —
 * no transition restriction, matching the backend's updateStatus
 * endpoint. Gated on case_triage:write.
 *
 * This component is rendered inline in the case-detail page header
 * (next to the case title), not inside its own Card/Stack like the
 * other case-triage cards. A full-width Alert would break that inline
 * layout, so errors surface as a compact inline red Text instead —
 * still clearly visible, but sized to fit next to a badge.
 */
export function CaseStatusPill({ caseId, status }: Props) {
  const canEdit = useHasPermission('case_triage:write');
  const updateStatus = useUpdateCaseStatus(caseId);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState<string | null>(status);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function openPicker() {
    setDraftStatus(status);
    setPickerOpen(true);
    setError(null);
  }

  function handleContinue() {
    if (!draftStatus) return;
    setPickerOpen(false);
    setPendingStatus(draftStatus);
  }

  function handleConfirm(reason: string) {
    if (!pendingStatus) return;
    setError(null);
    updateStatus.mutate(
      { status: pendingStatus as 'open' | 'awaiting_histology' | 'completed', reason },
      {
        onSuccess: () => setPendingStatus(null),
        onError: (err) => setError(parseMutationError(err, 'Could not update status.')),
      },
    );
  }

  return (
    <Group gap="xs" align="center">
      <Badge color={STATUS_COLOURS[status] ?? 'gray'} variant="light">
        {status}
      </Badge>
      {canEdit && (
        <Button size="xs" variant="subtle" onClick={openPicker}>
          Change status
        </Button>
      )}
      {error && (
        <Text role="alert" c="red" size="xs">
          {error}
        </Text>
      )}

      <Modal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Change status"
        centered
      >
        <Stack gap="md">
          <Select
            label="New status"
            data={ALL_STATUSES}
            value={draftStatus}
            onChange={setDraftStatus}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPickerOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleContinue} disabled={!draftStatus}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ReasonConfirmModal
        opened={pendingStatus !== null}
        title="Confirm action"
        description={`Force this case's status to "${pendingStatus ?? ''}".`}
        confirmLabel="Change status"
        isPending={updateStatus.isPending}
        onConfirm={handleConfirm}
        onClose={() => {
          setPendingStatus(null);
          setError(null);
        }}
      />
    </Group>
  );
}
